import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  Server,
  Shield,
  Activity,
  Cpu,
  ChevronDown,
  Router,
  Wifi,
  Lock,
  Layers,
  Database,
  Terminal,
  Globe,
  Code,
} from "lucide-react";

const IMAGES = {
  hero: "/assets/3770032_45ec_7.jpg",
  lab: "/assets/3710446_32b2_6.webp",
};

export default function CCNP() {
  const [activeTab, setActiveTab] = useState("Enterprise");

  useEffect(() => {
    AOS.init({ duration: 900, once: true, offset: 100 });
    window.scrollTo(0, 0);
  }, []);

  const curriculum = {
    Enterprise: [
      { title: "Advanced Routing (OSPF, BGP, EIGRP)", icon: Router },
      { title: "SD-WAN Implementation", icon: Wifi },
      { title: "MPLS & VPN Services", icon: Lock },
      { title: "Enterprise Network Troubleshooting", icon: Activity },
    ],
    Security: [
      { title: "Network Security Architectures", icon: Shield },
      { title: "VPNs & Secure Remote Access", icon: Lock },
      { title: "Next-Gen Firewalls & IPS", icon: Terminal },
      { title: "Identity & Access Policies", icon: Cpu },
    ],
    "Data Center": [
      { title: "Data Center Architecture & Switching", icon: Server },
      { title: "Storage Networking (SAN)", icon: Database },
      { title: "Automation with Cisco ACI", icon: Code },
      { title: "Cloud Integration", icon: Globe },
    ],
    Collaboration: [
      { title: "Unified Communications", icon: Wifi },
      { title: "VoIP & SIP Trunking", icon: Terminal },
      { title: "Video & Web Conferencing", icon: Layers },
      { title: "Collaboration Security", icon: Shield },
    ],
  };

  const labs = [
    {
      title: "Enterprise Lab",
      desc: "Multi-site BGP, OSPF, and MPLS configuration with redundancy.",
      icon: Router,
    },
    {
      title: "Security Lab",
      desc: "Configure VPNs, ACLs, and Next-Gen Firewalls in Cisco Packet Tracer.",
      icon: Shield,
    },
    {
      title: "Automation Lab",
      desc: "Deploy automation scripts with Cisco DNA Center and REST APIs.",
      icon: Cpu,
    },
  ];

  const skills = [
    {
      icon: Router,
      label: "Routing",
      description: "Master routing protocols like OSPF, BGP, and EIGRP.",
    },
    {
      icon: Server,
      label: "Switching",
      description: "Configure VLANs, STP, and Layer 2/3 switching.",
    },
    {
      icon: Shield,
      label: "Security",
      description: "Implement network security policies, ACLs, and VPNs.",
    },
    {
      icon: Cpu,
      label: "Automation",
      description: "Automate network tasks using scripts and APIs.",
    },
    {
      icon: Wifi,
      label: "Wireless",
      description: "Deploy and manage wireless networks securely.",
    },
    {
      icon: Lock,
      label: "VPNs",
      description: "Configure site-to-site and remote access VPNs.",
    },
  ];

  return (
    <div className="min-h-screen bg-white text-slate-800">
      {/* Hero */}
      <section className="relative grid place-items-center overflow-hidden  text-white py-24">
        <img
          src={IMAGES.hero}
          alt="CCNP"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="relative z-10 max-w-5xl px-6 text-center">
          <h1
            data-aos="fade-down"
            className="text-4xl sm:text-5xl font-extrabold"
          >
            Cisco Certified Network Professional (CCNP)
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            className="mt-4 mx-auto text-lg md:text-xl text-blue-100"
          >
            Take your networking expertise to the next level. Master advanced
            routing, security, automation, and data center technologies.
          </p>
          <div
            data-aos="zoom-in"
            data-aos-delay="400"
            className="mt-8 flex justify-center gap-4"
          >
            <Link
              to="/contact"
              className="rounded-xl bg-white px-6 py-3 font-semibold text-blue-700 shadow hover:scale-105 transition"
            >
              Enroll Now
            </Link>
          </div>
        </div>
      </section>

      {/* Skills */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <h2 className="text-3xl font-bold text-center mb-8">
          Skills You Will Master
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-6">
          {skills.map((s, i) => (
            <div
              key={i}
              className="flex flex-col items-center gap-2 rounded-lg bg-slate-100 p-4 hover:bg-blue-50 transition transform hover:scale-105"
              data-aos="flip-left"
            >
              <s.icon className="h-8 w-8 text-blue-600 animate-wiggle" />
              <span className="mt-1 font-semibold">{s.label}</span>
              <p className="text-center text-sm text-slate-600 mt-1">
                {s.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Curriculum Tabs */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <h3 className="text-2xl font-bold mb-6">Course Curriculum</h3>
        <div className="flex gap-4 flex-wrap">
          {Object.keys(curriculum).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 rounded-lg transition ${
                activeTab === tab
                  ? "bg-blue-600 text-white"
                  : "bg-slate-200 text-slate-700 hover:bg-slate-300"
              }`}
            >
              {tab}
            </button>
          ))}
        </div>
        <ul className="mt-6 space-y-3">
          {curriculum[activeTab].map((item, i) => (
            <li
              key={i}
              className="rounded-lg bg-slate-100 p-4 shadow-sm border border-slate-200 flex items-center gap-3"
              data-aos="fade-right"
            >
              <item.icon className="h-5 w-5 text-blue-600 animate-float" />
              {item.title}
            </li>
          ))}
        </ul>
      </section>

      {/* Labs */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <h3 className="text-2xl font-bold">Practical Labs</h3>
        <div className="mt-6 grid gap-6 md:grid-cols-3">
          {labs.map((lab, i) => (
            <div
              key={i}
              className="rounded-xl bg-slate-100 p-6 hover:shadow-lg transition border border-slate-200 flex items-start gap-3"
              data-aos="zoom-in"
            >
              <lab.icon className="h-10 w-10 text-cyan-600 animate-shake" />
              <div>
                <h4 className="font-semibold text-lg">{lab.title}</h4>
                <p className="text-slate-600 mt-1">{lab.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-8">
          <img
            src={IMAGES.lab}
            alt="Lab"
            className="rounded-2xl shadow-lg object-cover w-full h-72"
          />
        </div>
      </section>

      {/* FAQ */}
      <section className="mx-auto max-w-6xl px-6 py-12">
        <h3 className="text-2xl font-bold mb-6">Frequently Asked</h3>
        <div className="space-y-4">
          {[
            {
              q: "Do I need CCNA before CCNP?",
              a: "Yes, CCNA-level knowledge is required before attempting CCNP.",
            },
            {
              q: "Which CCNP specialization is best?",
              a: "Enterprise is the most popular, but Security and Data Center are highly in-demand.",
            },
            {
              q: "Is the exam included?",
              a: "Training is included. Exam vouchers need to be purchased separately.",
            },
          ].map((faq, i) => (
            <details
              key={i}
              className="group rounded-lg border border-slate-300 bg-slate-100 p-4"
            >
              <summary className="flex cursor-pointer items-center justify-between font-medium">
                {faq.q}{" "}
                <ChevronDown className="h-4 w-4 animate-float transition-transform group-open:rotate-180" />
              </summary>
              <p className="mt-2 text-sm text-slate-600">{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-6xl px-6 py-16">
        <div
          className="rounded-3xl bg-gradient-to-r from-blue-600 to-indigo-700 p-10 text-center shadow-xl relative overflow-hidden text-white"
          data-aos="zoom-in"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-cyan-400/10 to-purple-500/10 animate-pulse" />
          <h4 className="text-3xl font-bold relative z-10">
            Ready to become a Cisco Networking Professional?
          </h4>
          <p className="mt-3 text-blue-100 relative z-10">
            Specialize in Enterprise, Security, Data Center, or Collaboration
            with hands-on labs and expert guidance.
          </p>
          <div className="mt-6 flex justify-center gap-4 relative z-10">
            <Link
              to="/contact"
              className="rounded-full bg-white px-6 py-3 text-blue-700 font-semibold"
            >
              Talk to Counsellor
            </Link>
            <Link
              to="/contact"
              className="rounded-full border border-white/30 px-6 py-3 text-white hover:bg-white/10"
            >
              Apply Now
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
